import { formatDate } from "@intelligems/ig-utils/date-time";
import { getRemainingDaysUntil } from "@intelligems/ig-utils/stripe";

/**
 * The trial deadline as every merchant-facing surface sees it. A count never
 * travels without the date it was counted to, and `unknown` carries neither,
 * so a null `trialEnd` cannot be papered over with another field.
 */
export type TrialDeadline =
  | { state: "running"; remainingDays: number; endsAtLabel: string }
  | { state: "ends_today"; endsAtLabel: string }
  | { state: "ended" }
  | { state: "unknown" };

export const isTrialDeadlineAhead = (
  deadline: TrialDeadline
): deadline is Extract<TrialDeadline, { state: "running" | "ends_today" }> =>
  deadline.state === "running" || deadline.state === "ends_today";

export const resolveTrialDeadline = (
  trialEndAt: number | null | undefined
): TrialDeadline => {
  if (trialEndAt == null) {
    return { state: "unknown" };
  }

  const remainingDays = getRemainingDaysUntil(trialEndAt);
  const endsAtLabel = formatDate(trialEndAt);

  if (remainingDays > 0) {
    return { state: "running", remainingDays, endsAtLabel };
  }

  const isStillAhead = trialEndAt * 1_000 > Date.now();
  if (remainingDays === 0 && isStillAhead) {
    return { state: "ends_today", endsAtLabel };
  }

  return { state: "ended" };
};

const ENDING_SOON_DAYS = 7;

export const isTrialEndingSoon = (deadline: TrialDeadline): boolean =>
  deadline.state === "ends_today" ||
  (deadline.state === "running" && deadline.remainingDays <= ENDING_SOON_DAYS);
